import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Row, Col, Form as FormCh } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { ReactSelect, SpinnerLoader } from "@services/ui-components";
import { WearhousesModalProps } from "../../Warehouse/modals/modalsUtils";
import {
    useUpdateStorageProductHistoryMutation,
    useUpdateStorageProductRelationMutation,
} from "@me-team/host/main/warehouses_goods/graphql/warehouses_goods.hooks";
import * as Yup from "yup";
import QuantityInput from "../../AddNewProductList/AddProductListModal/QuantityInput";
import {
    CompanyAccess,
    StorageProductRelationActionType,
    StorageProductRelationHistoryAction,
    UpdateStorageProductRelationInput,
} from "@me-team/host/main/graphql/types";
import ModalProductInfo from "../components/ModalProductInfo";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import {
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
interface Option {
    value: string | number | string[];
    label: string;
}

interface ExtendedFormValues extends UpdateStorageProductRelationInput {
    moveAll: boolean;
}

const MoveGoodsModal: React.FC<WearhousesModalProps> = ({
    show,
    handleModal,
    refetch,
    warehouse,
    chosenProduct,
    warehouses,
    storageId,
    toggleShowToast,
    isEditMode = false,
    dataTour,
    canEditOperation,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { branchId } = useParams();
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const canMoveProduct = useRoleAccess(CompanyAccess.WarehouseProductMove);
    const canSubmit = isEditMode ? Boolean(canEditOperation) : canMoveProduct;
    const isDisabled = warehouse?.default || isEditMode;
    const tutorialActive = isWarehouseTutorialActive();
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (contentRef.current && dataTour) {
            contentRef.current.setAttribute("data-tour", dataTour);
        }
    }, [dataTour, show]);

    const initialValues: ExtendedFormValues = useMemo(
        () => ({
            action: StorageProductRelationActionType?.Movement,
            destinationStorage:
                isEditMode && chosenProduct?.relationStorage?.id ? chosenProduct.relationStorage.id : null,
            quantity: isEditMode ? (chosenProduct?.operationQuantity ?? null) : null,
            unit: isEditMode && chosenProduct?.operationUnit?.id ? chosenProduct.operationUnit.id : null,
            moveAll: false,
            comment: isEditMode && chosenProduct?.comment ? chosenProduct.comment : "",
        }),
        [chosenProduct, isEditMode]
    );

    const [unitOfMeasurements, setUnitOfMeasurements] = useState([]);

    useEffect(() => {
        const optionsUnitOfMeasurements =
            chosenProduct?.product?.units?.map((unit) => ({
                value: unit.id,
                label: unit.name,
            })) || [];

        setUnitOfMeasurements(optionsUnitOfMeasurements);
    }, [chosenProduct?.product]);
    const validationSchema = () =>
        Yup.object().shape({
            destinationStorage: Yup.string().required(t("Fill this field")),
            quantity: Yup.number().when("moveAll", {
                is: false,
                then: (schema) => {
                    if (chosenProduct?.quantity === 0) {
                        return schema.notRequired();
                    }
                    return schema.required(t("Fill this field"));
                },
                otherwise: (schema) => schema.notRequired(),
            }),
            unit: Yup.string().when("moveAll", {
                is: false,
                then: (schema) => {
                    if (chosenProduct?.quantity === 0) {
                        return schema.notRequired();
                    }
                    return schema.required(t("Fill this field"));
                },
                otherwise: (schema) => schema.notRequired(),
            }),
        });

    const [updateStorageProductRelation] = useUpdateStorageProductRelationMutation({
        context: {
            errorType: "local",
        },
    });
    const [updateStorageProductHistory] = useUpdateStorageProductHistoryMutation({
        context: {
            errorType: "local",
        },
    });
    const handleSubmit = async (
        values: typeof initialValues,
        { setFieldError }: FormikHelpers<typeof initialValues>
    ) => {
        if (!canSubmit) return;

        try {
            const { moveAll, unit, quantity, ...updatedValues } = values;
            let selectedUnitId = +unit;
            let selectedQuantity = +quantity;
            if (moveAll) {
                selectedQuantity = +chosenProduct?.quantity;

                const unitGroup = chosenProduct?.product?.alternativeUnit
                    ? chosenProduct?.product?.alternativeUnit?.unitGroup
                    : chosenProduct?.product?.unitGroup;

                selectedUnitId = unitGroup.units[0].id;
            }

            const input = {
                ...updatedValues,
                quantity: selectedQuantity,
                unit: selectedUnitId,
                destinationStorage: +values?.destinationStorage,
            };
            if (isEditMode) {
                if (currentBranchEmployeeId == null) return;
                await updateStorageProductHistory({
                    variables: {
                        input: [
                            {
                                storageProductRelationHistoryId: +chosenProduct?.id,
                                updateStorageProductRelationHistoryInputType: {
                                    quantity: +values?.quantity,
                                    comment: values.comment,
                                    moveAll: values.moveAll,
                                    unit: values.unit,
                                },
                            },
                        ],
                        branchId: Number(branchId),
                        employeeId: Number(currentBranchEmployeeId),
                        operationType: StorageProductRelationHistoryAction.Movement,
                    },
                });
                tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                tutorialActive && window.dispatchEvent(new Event("warehouseStepChanged"));
            } else {
                await updateStorageProductRelation({
                    variables: {
                        storageProductRelationId: +chosenProduct?.id,
                        input: input,
                        branchId: Number(branchId),
                    },
                });
                tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                tutorialActive && window.dispatchEvent(new Event("warehouseStepChanged"));
                tutorialActive && navigate(`/warehouse/reports/branch/${branchId}`);
            }
            refetch();
            handleModal();
            toggleShowToast(t("Saved"));
        } catch (error) {
            if (
                error.graphQLErrors?.[0].message === "Update of this operation will cause negative remnants on storage"
            ) {
                setFieldError(
                    "quantity",
                    t("In stock less than") +
                        " " +
                        values.quantity +
                        unitOfMeasurements.find((i) => i.value === values?.unit)?.label
                );
                return;
            } else {
                console.error(error);
            }
        }
    };

    const filteredWarehouses = warehouses?.filter((wh: Option) => +wh.value !== +storageId);

    return (
        <Modal
            size="lg"
            show={show}
            onHide={() => {
                tutorialActive && moveStepInLocalStorage(StepDirection.PREV);
                tutorialActive && window.dispatchEvent(new Event("warehouseStepChanged"));
                handleModal();
            }}
            centered
            dialogClassName={tutorialActive ? "tutorial-modal-scroll" : ""}
        >
            <div ref={contentRef}>
                <Modal.Header className="border-0" closeButton>
                    <h5 className="m-0">{t(isEditMode ? "Edit product movement" : "Move the product")}</h5>
                </Modal.Header>
                <Modal.Body>
                    {chosenProduct?.quantity === 0 ? (
                        <p className="mb-4 text-center text-danger">
                            {t(
                                "The selected product has a zero balance. Select the warehouse to which you are transferring the products."
                            )}
                        </p>
                    ) : (
                        ""
                    )}
                    <ModalProductInfo
                        date={chosenProduct?.date || moment().format("YYYY-MM-DD HH:mm:ss")}
                        productName={chosenProduct?.product?.name}
                        brand={chosenProduct?.product?.brand}
                        categoryName={chosenProduct?.product?.productCategoryNew.name}
                        sku={chosenProduct?.product?.sku}
                        barcode={chosenProduct?.product?.barcode}
                        unitGroupName={chosenProduct?.product?.unitGroup.name}
                        remnants={chosenProduct?.remnantsForRender || chosenProduct?.quantityForRender}
                    />
                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                        {({ values, errors, setFieldValue, isSubmitting }) => (
                            <Form>
                                <Row className={"mx-0 mt-3"}>
                                    <Col sm={3} className="d-flex pr-0 mb-2 mb-sm-0">
                                        <label
                                            htmlFor="destinationStorage"
                                            className="d-flex align-items-center fw-bold fs-7 mb-0 text-dark form-label required text-break"
                                            style={{ maxHeight: "49px" }}
                                        >
                                            <span className="required-label">{t("Warehouse name")}</span>
                                        </label>
                                    </Col>

                                    <Col sm={9}>
                                        <ReactSelect
                                            name="destinationStorage"
                                            options={filteredWarehouses}
                                            onChange={(option) => setFieldValue("destinationStorage", option.value)}
                                            disabled={isDisabled}
                                            placeholder={t("Select a warehouse")}
                                            isSearchable
                                            value={filteredWarehouses?.find(
                                                (option: Option) => option.value === values.destinationStorage
                                            )}
                                        />
                                        <ErrorMessage
                                            name="destinationStorage"
                                            component="div"
                                            className="fs-7 m-0 p-1 text-danger"
                                        />
                                        {chosenProduct?.quantity === 0 ? (
                                            ""
                                        ) : (
                                            <FormCh.Check
                                                className=" mt-3"
                                                type={"checkbox"}
                                                label={<span className="fs-7 text-dark">{t("Move All")}</span>}
                                                checked={values?.moveAll}
                                                onChange={(e) => setFieldValue("moveAll", e.target.checked)}
                                            />
                                        )}
                                    </Col>
                                </Row>
                                {chosenProduct?.quantity === 0 ? (
                                    ""
                                ) : (
                                    <>
                                        <Row className="pt-3 mx-0">
                                            <Col sm={3} className="d-flex pr-0">
                                                <label
                                                    htmlFor="quantity"
                                                    className="d-flex align-items-center mb-0 text-dark form-label required text-break mb-2 mb-sm-0"
                                                    style={{ maxHeight: "49px" }}
                                                >
                                                    <span className="text-dark fw-bold fs-7 required-label">
                                                        {t("Amount to be moved")}
                                                    </span>
                                                </label>
                                            </Col>
                                            <Col sm={9}>
                                                <QuantityInput
                                                    name="quantity"
                                                    id="addsProduct-quantity-field"
                                                    placeholder={t("Amount to be moved")}
                                                    maxLength={9}
                                                    isDisabled={values?.moveAll}
                                                    isMoveAll={values?.moveAll}
                                                    isAllowZero={isEditMode}
                                                />
                                                <ErrorMessage
                                                    name="quantity"
                                                    component="div"
                                                    className="fs-7 m-0 p-1 text-danger"
                                                />
                                            </Col>
                                        </Row>
                                        <Row className="pt-3 mb-3 mx-0">
                                            <Col sm={3} className="d-flex pr-0 mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="unit"
                                                    className="d-flex align-items-center mb-0 fw-bold fs-7 text-dark form-label required text-break required-label"
                                                    style={{ maxHeight: "49px" }}
                                                >
                                                    {t("Unit of measurements")}
                                                </label>
                                            </Col>
                                            <Col sm={9}>
                                                <ReactSelect
                                                    id="addProduct-unit-field"
                                                    name="unit"
                                                    placeholder={t("Select an unit")}
                                                    options={unitOfMeasurements}
                                                    value={
                                                        values?.moveAll
                                                            ? null
                                                            : unitOfMeasurements?.find(
                                                                  (option: Option) => option.value === values.unit
                                                              )
                                                    }
                                                    onChange={(selectedOption: Option) =>
                                                        setFieldValue("unit", selectedOption.value)
                                                    }
                                                    isSearchable={false}
                                                    disabled={values.moveAll}
                                                />

                                                <ErrorMessage name="unit">
                                                    {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                                <Row className="mb-3 mx-0">
                                    <Col sm={3} className="pr-0 mb-2 mb-sm-0">
                                        <label
                                            htmlFor="comment"
                                            className="my-0 pt-sm-3 text-dark form-label text-break fw-bold fs-7 line-height-140"
                                        >
                                            {t("Comment")}
                                        </label>
                                    </Col>
                                    <Col sm={9}>
                                        <Field
                                            as="textarea"
                                            name="comment"
                                            placeholder={t("For example, enter the invoice number")}
                                            className={`fs-7 form-control ${errors?.comment ? "is-invalid" : ""}`}
                                            maxLength={400}
                                        />
                                        <span
                                            className={`fs-7 ${
                                                values.comment.length > 400 ? "text-danger" : "text-grey"
                                            }`}
                                        >
                                            {values.comment.length}/400
                                        </span>
                                        <ErrorMessage name="comment" component="div" className="text-danger" />
                                    </Col>
                                </Row>

                                <Row className="d-flex justify-content-end pt-6">
                                    <Col xs={6} sm="auto" className="mb-sm-0 pe-1">
                                        <Button
                                            variant="outline-primary"
                                            className="fw-normal custom-btn rounded-1"
                                            onClick={() => {
                                                tutorialActive && moveStepInLocalStorage(StepDirection.PREV);
                                                tutorialActive &&
                                                    window.dispatchEvent(new Event("warehouseStepChanged"));
                                                handleModal();
                                            }}
                                        >
                                            {t("Cancel")}
                                        </Button>
                                    </Col>
                                    <Col xs={6} sm="auto" className="mb-sm-0 ps-1">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="fw-bold custom-btn rounded-1"
                                            disabled={isSubmitting || !canSubmit}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                            ) : (
                                                t(isEditMode ? "Save" : "servicesPage.moveModal.moveBtn")
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </div>
        </Modal>
    );
};

export default MoveGoodsModal;
